'use client';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export function trackEvent(eventName: string, eventParams: Record<string, unknown> = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
}

// ─── Donation Funnel Events ───────────────────────────────────────────────────

/** Step 0: User lands on donation screen */
export function trackDonationStart() {
  trackEvent('donation_funnel_start', {
    event_category: 'Donation Funnel',
    event_label: 'Donation page viewed',
  });
}

/** Step 0→1: User selects a cause */
export function trackCauseSelected(cause: string) {
  trackEvent('donation_cause_selected', {
    event_category: 'Donation Funnel',
    event_label: cause,
    cause_name: cause,
  });
}

/** Step 1→2: User selects frequency and amount */
export function trackAmountSelected(amount: number, frequency: string) {
  trackEvent('donation_amount_selected', {
    event_category: 'Donation Funnel',
    event_label: `${frequency} - $${amount}`,
    value: amount,
    currency: 'USD',
    frequency,
  });
}

/** Step 2→3: User submits donor details */
export function trackDonorDetailsSubmitted() {
  trackEvent('donation_details_submitted', {
    event_category: 'Donation Funnel',
    event_label: 'Donor details step completed',
  });
}

/** Step 3: Payment initiated */
export function trackPaymentInitiated(amount: number, cause: string, frequency: string) {
  trackEvent('begin_checkout', {
    event_category: 'Donation Funnel',
    event_label: `${cause} - ${frequency}`,
    value: amount,
    currency: 'USD',
    cause_name: cause,
    frequency,
  });
}

/** Payment succeeded */
export function trackDonationComplete(amount: number, cause: string, frequency: string, transactionId?: string) {
  trackEvent('purchase', {
    event_category: 'Donation Funnel',
    event_label: `${cause} - ${frequency}`,
    value: amount,
    currency: 'USD',
    transaction_id: transactionId ?? '',
    cause_name: cause,
    frequency,
  });
}

/** User dropped off at a step */
export function trackFunnelDropOff(step: number, stepName: string) {
  trackEvent('donation_funnel_drop_off', {
    event_category: 'Donation Funnel',
    event_label: stepName,
    funnel_step: step,
    step_name: stepName,
  });
}

// ─── Member Signup Flow Events ────────────────────────────────────────────────

/** User views signup page */
export function trackSignupStart() {
  trackEvent('signup_start', {
    event_category: 'Member Signup',
    event_label: 'Signup page viewed',
  });
}

/** User submits signup form */
export function trackSignupSubmitted() {
  trackEvent('signup_submitted', {
    event_category: 'Member Signup',
    event_label: 'Signup form submitted',
  });
}

/** User completes email verification */
export function trackSignupVerified() {
  trackEvent('signup_verified', {
    event_category: 'Member Signup',
    event_label: 'Email verified',
  });
}

/** User completes onboarding */
export function trackOnboardingComplete(membershipTier?: string) {
  trackEvent('onboarding_complete', {
    event_category: 'Member Signup',
    event_label: membershipTier ?? 'unknown',
    membership_tier: membershipTier ?? 'unknown',
  });
}

// ─── Engagement Events ────────────────────────────────────────────────────────

/** User clicks "Donate Now" CTA anywhere */
export function trackDonateNowClick(location: string) {
  trackEvent('donate_cta_click', {
    event_category: 'Engagement',
    event_label: location,
    click_location: location,
  });
}

/** User subscribes to newsletter */
export function trackNewsletterSignup() {
  trackEvent('newsletter_signup', {
    event_category: 'Engagement',
    event_label: 'Newsletter subscription',
  });
}

/** User views a cause card */
export function trackCauseViewed(cause: string) {
  trackEvent('cause_viewed', {
    event_category: 'Engagement',
    event_label: cause,
    cause_name: cause,
  });
}

/** User logs in */
export function trackLogin() {
  trackEvent('login', {
    event_category: 'Engagement',
    event_label: 'User logged in',
  });
}
